import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../../redux/orebiSlice";
import { ShoppingCart, ChevronRight, ZoomIn } from "lucide-react";
import toast from 'react-hot-toast';
import { useTranslation } from "react-i18next";

const ProductDetails = () => {
  const location = useLocation();
  const [prevLocation, setPrevLocation] = useState("");
  const [productInfo, setProductInfo] = useState([]);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    setProductInfo(location.state.item);
    setPrevLocation(location.pathname);
    setTotalPrice(location.state.item.price);
  }, [location, productInfo]);

  const handleQuantityChange = (value) => {
    const newQuantity = Math.max(1, value); // Ensure minimum quantity is 1
    setQuantity(newQuantity);
    setTotalPrice((productInfo.price * newQuantity).toFixed(2));
  };

  const handleAddToCart = () => {
    dispatch(
      addToCart({
        _id: productInfo._id,
        name: productInfo.productName,
        quantity: quantity,
        image: productInfo.img,
        badge: productInfo.badge,
        price: productInfo.price,
      })
    );
    setAddedToCart(true);
    toast.success(`${productInfo.productName} added to cart!`, {
      position: 'bottom-right',
      style: {
        background: '#333',
        color: '#fff',
      },
      iconTheme: {
        primary: '#4ade80',
        secondary: '#fff',
      },
    });
    setTimeout(() => setAddedToCart(false), 2000);
  };

  console.log(productInfo);

  return (
    <div className="w-full mx-auto bg-gray-50">
      {/* Breadcrumb */}
      <nav className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center space-x-2 text-gray-600 text-sm">
          <Link to="/" className="hover:text-gray-900">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/category/${productInfo.catId}`} className="hover:text-gray-900">{productInfo.catName}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">{productInfo.productName}</span>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-8 p-6 md:p-8">
            {/* Image Section with Zoom */}
            <div className="xl:col-span-2 h-full">
              <div
                className="aspect-square rounded-xl overflow-hidden bg-gray-100 relative cursor-zoom-in"
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
              >
                <img
                  className={`w-full h-full object-cover transition-transform duration-300 ${isZoomed ? 'scale-110' : 'scale-100'
                    }`}
                  src={productInfo.img}
                  alt={productInfo.productName}
                />
                <div className="absolute top-4 right-4 bg-white/80 p-2 rounded-full">
                  <ZoomIn className="w-5 h-5 text-gray-600" />
                </div>
              </div>
            </div>

            {/* Product Info Section */}
            <div className="xl:col-span-4 flex flex-col gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    In Stock
                  </span>
                  {productInfo.badge && (
                    <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      New Arrival
                    </span>
                  )}
                </div>
                <div className="flex justify-between text-sm">
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {productInfo.productName}
                  </h1>
                  <div className="flex gap-x-2.5 text-sm">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                      {productInfo.recipe == 1 ? t("common.kg_price") : ''}
                    </h3>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                      {productInfo.recipe == 1 ? `EGP ${productInfo.displayPrice}` : ''}
                    </h3>
                  </div>


                </div>


                <div className="flex items-baseline gap-4">
                  <span className="text-3xl font-bold text-gray-900">
                    EGP {totalPrice}
                  </span>

                  {productInfo.recipe == 1 ? null : (
                    <span className="text-sm text-gray-500">
                      / {productInfo.unitValue} {productInfo.unitName}
                    </span>
                  )}
                </div>

                {/* Add Quantity Selector if recipe is 1 */}
                {productInfo.recipe === 1 && (
                  <div className="flex items-center gap-4 mt-4">
                    <label className="text-gray-700 font-medium"> {t("home.quantity")}: </label>
                    <div className="flex items-center border rounded-md">
                      <button
                        className="px-3 py-1 border-r hover:bg-gray-100"
                        onClick={() => handleQuantityChange(quantity - 1)}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                        className="w-16 text-center py-1 focus:outline-none"
                      />
                      <button
                        className="px-3 py-1 border-l hover:bg-gray-100"
                        onClick={() => handleQuantityChange(quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                    <label className="text-gray-700 font-medium">{productInfo.unitName}</label>
                  </div>
                )}
              </div>

              {/* Product Specifications */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">{t("home.Specifications")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-start text-sm">
                      <span className="text-gray-600">{t("home.categories")}: </span>
                      <span className="font-medium">{productInfo.catName}</span>
                    </div>
                    <div className="flex justify-start text-sm">
                      <span className="text-gray-600">{t("home.unitName")}: </span>
                      <span className="font-medium"> {productInfo.unitName}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-start text-sm">
                      <span className="text-gray-600">{t("home.unitValue")}: {" "}</span>
                      <span className="font-medium"> {productInfo.unitValue}</span>
                    </div>

                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">{t("home.Details")}</h3>
                <p className="text-gray-600 leading-relaxed">{productInfo.des}</p>
              </div>

              {/* Add to Cart Button */}
              <div className="mt-auto">
                <button
                  onClick={handleAddToCart}
                  disabled={addedToCart}
                  className={`w-full py-4 px-6 flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-300 ${addedToCart
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-black hover:bg-gray-800'
                    } text-white`}
                >
                  {addedToCart ? (
                    <>
                      <svg className="w-6 h-6 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("cart.added_to_cart")}
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      {t("cart.add_to_cart")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;